import React, {
  Fragment,
  useCallback,
  useEffect,
  useState,
  useMemo,
  useRef,
} from "react";
import { useOutletContext } from "react-router-dom";
import AlertBanner from "../../../../../components/ui/alerts/AlertBanner";
import GhostCardContainer from "../../../../../components/ui/loading/skeletons/GhostCardContainer";
import GhostChart from "../../../../../components/ui/loading/skeletons/GhostChart";
import LoadingToRealTransitionWrapper from "../../../../../components/ui/transitions/LoadingToRealTransitionWraper";
import InstitutionDataLoaderBanner from "./common/InsitutionDataLoaderBanner";
import InstitutionPartialDataWarningBanner from "./common/InstitutionPartialDataWarningBanner";
import CustomTransactionDateRangeModal from "../../../../../components/ui/popup/finances/CustomTransactionDateRangeModal";
import { useRefreshOnAuth } from "../../../../../hooks/useRefreshOnAuth";
import { getLanguageContent } from "../../../../../utils/language/languageSelector";
import { PERSONAL_FINANCES_SCHEMA } from "../../../../../utils/schemas/language/pages/personalFinancesSchema";
import { useIncomeUsage } from "../../../../../hooks/finances/useIncomeUsage";
import HistoryChart from "../../../../../components/ui/charts/HistoryChart";
import IncomeDecisionReviewModal from "../../../../../components/ui/popup/finances/IncomeDecisionReviewModal";
import TransactionDetailsModal from "../../../../../components/ui/popup/finances/TransactionDetailsModal";

const INCOME_DATE_FILTERS = [
  { label: "1M", value: "1M" },
  { label: "3M", value: "3M" },
  { label: "6M", value: "6M" },
  { label: "1Y", value: "1Y" },
  { label: "ALL", value: "ALL" },
  { label: "Custom", value: "custom" },
];

function PersonalIncome() {
  const content = getLanguageContent(PERSONAL_FINANCES_SCHEMA);

  const { institutionSyncStates = [], loadSyncStates } =
    useOutletContext() || {};

  const isEmptyOnboarding = useMemo(() => {
    if (institutionSyncStates.length === 0) return false;
    return institutionSyncStates.every(
      (inst) => inst.transaction_first_load_completed === false,
    );
  }, [institutionSyncStates]);

  const isPartialSyncing = useMemo(() => {
    if (isEmptyOnboarding || institutionSyncStates.length === 0) return false;
    return institutionSyncStates.some(
      (inst) => inst.transaction_history_completed === false,
    );
  }, [institutionSyncStates, isEmptyOnboarding]);

  const processingInstitutionsText = useMemo(() => {
    const pendingNames = institutionSyncStates
      .filter((inst) => inst.transaction_history_completed === false)
      .map((inst) => inst.institution_name || "Unknown Institution");

    if (pendingNames.length === 0) return "";
    if (pendingNames.length === 1) return pendingNames[0];
    if (pendingNames.length === 2)
      return `${pendingNames[0]} & ${pendingNames[1]}`;
    return `${pendingNames.slice(0, -1).join(", ")}, & ${
      pendingNames[pendingNames.length - 1]
    }`;
  }, [institutionSyncStates]);

  const [isCustomDateModalOpen, setIsCustomDateModalOpen] = useState(false);
  const [isDecisionModalOpen, setIsDecisionModalOpen] = useState(false);

  const {
    appliedPeriod,
    setAppliedPeriod,
    appliedCustomDateRange,
    setAppliedCustomDateRange,
    isLoading,
    isPending,
    error,
    incomeData,
    formattedChartData,
    selectedTransaction,
    setSelectedTransaction,
    fetchIncome,
    handleBarClick,
  } = useIncomeUsage("3M");

  useEffect(() => {
    fetchIncome();
  }, [fetchIncome]);

  useRefreshOnAuth(fetchIncome);

  const handlePeriodChange = (filterValue) => {
    if (filterValue === "custom") {
      setIsCustomDateModalOpen(true);
      return;
    }
    setAppliedPeriod(filterValue);
    setAppliedCustomDateRange(null);
    setIsCustomDateModalOpen(false);
  };

  const handleApplyCustomDateRange = (range) => {
    setAppliedCustomDateRange(range);
    setAppliedPeriod("custom");
    setIsCustomDateModalOpen(false);
  };

  if (isEmptyOnboarding) {
    return (
      <InstitutionDataLoaderBanner
        content={content.status.transactions.no_data}
        refreshContent={content.global.refresh_in}
        onRefresh={loadSyncStates}
      />
    );
  }

  return (
    <Fragment>
      {isPartialSyncing && (
        <InstitutionPartialDataWarningBanner
          onRefresh={loadSyncStates}
          content={content.status.transactions.processing}
          processingText={processingInstitutionsText}
          refreshText={content.global.refresh}
        />
      )}

      {error && <p className="text-danger">{error}</p>}

      <LoadingToRealTransitionWrapper
        isLoading={isLoading}
        ghost={
          <GhostCardContainer>
            <GhostChart />
          </GhostCardContainer>
        }
      >
        <Fragment>
          {incomeData.candidates?.length > 0 && (
            <AlertBanner
              title="Income Verification Required"
              message={`You have ${incomeData.candidates.length} deposit transaction(s) that might be income.`}
              variant="warning"
              actionButton={
                <button
                  onClick={() => setIsDecisionModalOpen(true)}
                  className="cta-button primary small rounded"
                >
                  Review
                </button>
              }
              style={{ marginBottom: "16px" }}
            />
          )}

          <div className="standard-card-container mobile-reset-standard-card-container gap-reset mobile-white-box">
            {/* HEADER ROW WITH PERIOD FILTERS */}
            <div
              style={{
                display: "flex",
                width: "100%",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "12px",
              }}
            >
              <div className="income-header-row">
                <span className="income-label">Total Income</span>
                <h2
                  className={`income-amount ${
                    isLoading ? "is-refreshing" : ""
                  }`}
                >
                  $
                  {parseFloat(incomeData.summary?.total || 0).toLocaleString(
                    "en-US",
                    { minimumFractionDigits: 2 },
                  )}
                </h2>
              </div>

              {/* DATE FILTER PILLS */}
              <div className="utc-header-filtering">
                {INCOME_DATE_FILTERS.map((filter) => {
                  const isActive = appliedPeriod === filter.value;
                  const isCustomApplied =
                    filter.value === "custom" &&
                    appliedPeriod === "custom" &&
                    appliedCustomDateRange;

                  let displayLabel = filter.label;
                  if (isCustomApplied) {
                    displayLabel = `${appliedCustomDateRange.start_date} - ${appliedCustomDateRange.end_date}`;
                  }

                  return (
                    <div
                      key={filter.value}
                      className={`utc-header-filtering-item ${
                        isActive ? "active" : ""
                      }`}
                      onClick={() => handlePeriodChange(filter.value)}
                      style={{ cursor: "pointer" }}
                    >
                      {displayLabel}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* CHART REMAINS IN ORIGINAL CARD WITH SUBTLE REFRESH OPACITY */}
            <div style={{ width: "100%", minWidth: 0, overflowX: "hidden" }}>
              {isLoading || isPending ? (
                <GhostChart height={220} />
              ) : (
                <HistoryChart
                  key={`${appliedPeriod}-${formattedChartData.length}`}
                  data={formattedChartData}
                  height={220}
                  showLegend={false}
                  showYAxis={false}
                  showXAxisLabels={true}
                  defaultBarColor="#10B981"
                  referenceLines={[]}
                  onBarClick={handleBarClick}
                  emptyMsg="No historical income records available for this period."
                  showBarValues={true}
                />
              )}
            </div>
          </div>
        </Fragment>
      </LoadingToRealTransitionWrapper>

      {/* Modals */}
      {isDecisionModalOpen && (
        <IncomeDecisionReviewModal
          title="Income Verification"
          transactions={incomeData.candidates}
          onClose={() => setIsDecisionModalOpen(false)}
          onSuccess={fetchIncome}
        />
      )}

      {selectedTransaction && (
        <TransactionDetailsModal
          transaction={selectedTransaction}
          onClose={() => setSelectedTransaction(null)}
          onTransactionUpdate={() => fetchIncome()}
          copy={content.transactions?.details}
          commonCopy={content.global}
        />
      )}

      {isCustomDateModalOpen && (
        <CustomTransactionDateRangeModal
          isOpen={isCustomDateModalOpen}
          onClose={() => setIsCustomDateModalOpen(false)}
          onApply={handleApplyCustomDateRange}
          initialFromDate={appliedCustomDateRange?.start_date || ""}
          initialToDate={appliedCustomDateRange?.end_date || ""}
          copy={content.transactions?.date_filters?.modal}
        />
      )}
    </Fragment>
  );
}

export default PersonalIncome;
